import React, { useEffect } from "react";
import { useRouter } from "next/navigation";
import { CreditCard, LogOut, Settings, User, Computer, Key } from "lucide-react";
import { useAuth } from "@/components/providers/auth-context";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Link from "next/link";

export function UserNav() {
  const { userProfile, logOut, getIdToken } = useAuth();
  const router = useRouter();

  // Log token when component mounts and user is available
  useEffect(() => {
    if (userProfile) {
      const logToken = async () => {
        try {
          const token = await getIdToken();
          console.log('Firebase JWT Token:', token);
        } catch (error) {
          console.error('Error getting token in UserNav:', error);
        }
      };
      logToken();
    }
  }, [userProfile, getIdToken]);

  if (!userProfile) return null;

  const initials = userProfile.displayName
    ? userProfile.displayName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
    : "";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarImage src={userProfile.photoURL} alt={userProfile.displayName} />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{userProfile.displayName}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {userProfile.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <Link href="/profile/dashboard">
            <DropdownMenuItem>
              <User className="mr-2 h-4 w-4" />
              <span>Dashboard</span>
            </DropdownMenuItem>
          </Link>
          <Link href="/profile/settings">
            <DropdownMenuItem>
              <Settings className="mr-2 h-4 w-4" />
              <span>Settings</span>
            </DropdownMenuItem>
          </Link>
          <Link href="/profile/usage">
            <DropdownMenuItem>
              <CreditCard className="mr-2 h-4 w-4" />
              <span>Usage</span>
            </DropdownMenuItem>
          </Link>
          <Link href="/profile/rate-limiter">
            <DropdownMenuItem>
              <Computer className="mr-2 h-4 w-4" />
              <span>Rate Limits</span>
            </DropdownMenuItem>
          </Link>
          <Link href="/profile/api-keys">
            <DropdownMenuItem>
              <Key className="mr-2 h-4 w-4" />
              <span>API Keys</span>
            </DropdownMenuItem>
          </Link>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={async () => {
          await logOut();
          router.push('/');
        }}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
